import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import ServiceLandingPage from "./pages/ServiceLandingPage";
import { LANDING_PAGES } from "./data/landingPages";
import BLOG_POSTS from "./data/blogPosts.json";

const Booking = React.lazy(() => import("./pages/Booking"));
const BookingSuccess = React.lazy(() => import("./pages/BookingSuccess"));
const Memberships = React.lazy(() => import("./pages/Memberships"));
const HairRestoration = React.lazy(() => import("./pages/HairRestoration"));
const About = React.lazy(() => import("./pages/About"));
const FAQs = React.lazy(() => import("./pages/FAQs"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Financing = React.lazy(() => import("./pages/Financing"));
const Shop = React.lazy(() => import("./pages/Shop"));
const Education = React.lazy(() => import("./pages/Education"));
const Blog = React.lazy(() => import("./pages/Blog"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const AdminLogin = React.lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));

function ScrollToTop() {
    const { pathname } = useLocation();
    React.useEffect(() => {
        if ("scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual";
        }
        const scroll = () => {
            // Plain scrollTo(0,0) is universally instant — works on iOS Safari too
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        };
        scroll();
        const t1 = requestAnimationFrame(scroll);
        const t2 = setTimeout(scroll, 60);
        const t3 = setTimeout(scroll, 200);
        return () => {
            cancelAnimationFrame(t1);
            clearTimeout(t2);
            clearTimeout(t3);
        };
    }, [pathname]);
    return null;
}

function PublicShell({ children }) {
    return <Layout><React.Suspense fallback={<div className="min-h-[70vh]" />}>{children}</React.Suspense></Layout>;
}

// SEO landing page renderer for a fixed slug.
function LandingRoute({ slug }) {
    const data = LANDING_PAGES[slug];
    if (!data) return <Navigate to="/services" replace />;
    return <ServiceLandingPage data={data} />;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <ScrollToTop />
                    <React.Suspense fallback={<div className="min-h-screen bg-[#F2EDE2]" />}>
                    <Routes>
                        <Route path="/" element={<PublicShell><Home /></PublicShell>} />
                        <Route path="/services" element={<PublicShell><Services /></PublicShell>} />
                        <Route path="/services/:slug" element={<PublicShell><ServiceDetail /></PublicShell>} />
                        <Route path="/book/success" element={<PublicShell><BookingSuccess /></PublicShell>} />
                        <Route path="/book/:slug" element={<PublicShell><Booking /></PublicShell>} />
                        <Route path="/memberships" element={<PublicShell><Memberships /></PublicShell>} />
                        <Route path="/hair-restoration" element={<PublicShell><HairRestoration /></PublicShell>} />
                        <Route path="/about" element={<PublicShell><About /></PublicShell>} />
                        <Route path="/faqs" element={<PublicShell><FAQs /></PublicShell>} />
                        <Route path="/contact" element={<PublicShell><Contact /></PublicShell>} />
                        <Route path="/financing" element={<PublicShell><Financing /></PublicShell>} />
                        <Route path="/shop" element={<PublicShell><Shop /></PublicShell>} />
                        <Route path="/education" element={<PublicShell><Education /></PublicShell>} />
                        <Route path="/blog" element={<PublicShell><Blog /></PublicShell>} />
                        {BLOG_POSTS.map((p) => (
                            <Route key={p.slug} path={`/${p.slug}`} element={<PublicShell><BlogPost slug={p.slug} /></PublicShell>} />
                        ))}
                        {/* SEO landing pages — canonical URLs use -san-diego-ca suffix */}
                        <Route path="/microneedling-san-diego-ca" element={<PublicShell><LandingRoute slug="microneedling-san-diego-ca" /></PublicShell>} />
                        <Route path="/medical-weight-loss-san-diego-ca" element={<PublicShell><LandingRoute slug="medical-weight-loss-san-diego-ca" /></PublicShell>} />
                        <Route path="/neurotoxins-san-diego-ca" element={<PublicShell><LandingRoute slug="neurotoxins-san-diego-ca" /></PublicShell>} />
                        <Route path="/iv-therapy-san-diego-ca" element={<PublicShell><LandingRoute slug="iv-therapy-san-diego-ca" /></PublicShell>} />
                        <Route path="/im-shots-san-diego-ca" element={<PublicShell><LandingRoute slug="im-shots-san-diego-ca" /></PublicShell>} />
                        <Route path="/facial-services-san-diego-ca" element={<PublicShell><LandingRoute slug="facial-services-san-diego-ca" /></PublicShell>} />
                        <Route path="/peptide-therapy-san-diego-ca" element={<PublicShell><LandingRoute slug="peptide-therapy-san-diego-ca" /></PublicShell>} />
                        {/* Legacy slugs — redirect to canonical -san-diego-ca URLs */}
                        <Route path="/microneedling-san-diego" element={<Navigate to="/microneedling-san-diego-ca" replace />} />
                        <Route path="/medical-weight-loss-san-diego" element={<Navigate to="/medical-weight-loss-san-diego-ca" replace />} />
                        <Route path="/neurotoxins-san-diego" element={<Navigate to="/neurotoxins-san-diego-ca" replace />} />
                        <Route path="/iv-therapy-san-diego" element={<Navigate to="/iv-therapy-san-diego-ca" replace />} />
                        <Route path="/im-shots-san-diego" element={<Navigate to="/im-shots-san-diego-ca" replace />} />
                        <Route path="/facials-san-diego" element={<Navigate to="/facial-services-san-diego-ca" replace />} />
                        <Route path="/booking" element={<Navigate to="/services" replace />} />
                        <Route path="/services/glow-bundle" element={<Navigate to="/services/signature-bundles" replace />} />
                        <Route path="/services/energy-metabolism" element={<Navigate to="/services/signature-bundles" replace />} />
                        <Route path="/services/cellular-recharge" element={<Navigate to="/services/signature-bundles" replace />} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin" element={<AdminDashboard />} />
                        {/* Catch-all: prevents blank screens on unknown URLs */}
                        <Route path="*" element={<Navigate to="/services" replace />} />
                    </Routes>
                    </React.Suspense>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
